import React, { useMemo } from 'react';
import { 
  Download, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  X, 
  FileText,
  Loader2
} from 'lucide-react';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';

interface DownloadProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DownloadProgressModal: React.FC<DownloadProgressModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { 
    activeDownloads, 
    removeDownload, 
    clearAllDownloads 
  } = useDownloadProgress();

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: true, 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'initiated':
        return 'Initiated';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'in_progress':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const completedCount = activeDownloads.filter(d => d.status === 'completed').length;
  const failedCount = activeDownloads.filter(d => d.status === 'failed').length;
  const inProgressCount = activeDownloads.filter(d => d.status === 'in_progress').length;

  // Sort downloads by startTime in descending order (newest first)
  const sortedDownloads = useMemo(() => {
    return [...activeDownloads].sort((a, b) => {
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
    });
  }, [activeDownloads]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden z-[9997]" style={{ zIndex: 9997 }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Download Progress
           
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{inProgressCount}</div>
                <div className="text-sm text-gray-600">In Progress</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{completedCount}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{failedCount}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>
          </div>

          {/* Download List */}
          {sortedDownloads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No active downloads</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedDownloads.map((download) => (
                <div
                  key={download.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(download.status)}
                        <span className={`font-medium ${getStatusColor(download.status)}`}>
                          {getStatusText(download.status)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatTime(download.startTime)}
                        </span>
                      </div>
                      
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {download.deviceName}
                      </div>
                      
                      <div className="text-xs text-gray-500 mb-2">
                        Device ID: {download.deviceId}
                      </div>

                      {/* Progress Bar */}
                      {download.status === 'in_progress' && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>{download.progress}% complete</span>
                            <span>
                              {download.downloadedFiles} of {download.totalFiles || '?'} files
                            </span>
                          </div>
                          <Progress value={download.progress} className="h-2" />
                          {download.currentFile && (
                            <div className="text-xs text-gray-500 truncate">
                              Downloading: {download.currentFile}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error Message */}
                      {download.status === 'failed' && download.error && (
                        <div className="text-sm text-readable-red bg-[rgba(196,0,31,0.06)] p-2 rounded mt-2">
                          Error: {download.error}
                        </div>
                      )}

                      {/* Success Message */}
                      {download.status === 'completed' && (
                        <div className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                          Successfully downloaded {download.totalFiles || 1} file(s)
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDownload(download.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllDownloads}
                disabled={activeDownloads.length === 0}
              >
                Clear All
              </Button>
              <Button
              //  variant="outline"
                size="sm"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
      </DialogContent>
    </Dialog>
  );
};
